import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon, 
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.js';

const Navbar = () => {
  const { user, loading } = useAuth(); 
  const navigate = useNavigate();
  const location = useLocation();
  const supportRef = useRef(null);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0);

  // Auto-close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSupportOpen(false);
  }, [location.pathname]);

  // Handle clicks outside the Support dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (supportRef.current && !supportRef.current.contains(event.target)) {
        setIsSupportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check for Dashboard/Admin routes
  const isDashboardRoute = location.pathname.startsWith('/dashboard') || 
                           location.pathname.startsWith('/admin') || 
                           location.pathname.startsWith('/team-dashboard');

  const handleLogoClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount >= 4) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  // --- STRICT LOGIC SECTION ---
  
  // 1. If auth is still checking, return nothing (prevents flickering)
  if (loading) return null;

  // 2. If user is logged in, return nothing (Total disappearance)
  if (user) return null;

  // 3. Safety check: If we are on a dashboard route anyway, return nothing
  if (isDashboardRoute && location.pathname !== '/admin/login') return null;

  // --- PUBLIC GUEST NAVBAR STARTS HERE ---
  return (
    <nav className="bg-[#050505]/90 border-b border-white/5 sticky top-0 z-50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">

          {/* LOGO */}
<div className="flex items-center cursor-pointer space-x-3 group select-none" onClick={handleLogoClick}>
  <div className="relative">
    {/* Animated Glow Effect */}
    <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
    
    <div className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-[#0a0a0a] border border-white/10 flex items-center justify-center text-white font-black shadow-2xl transition-transform group-hover:scale-110">
      <span className="text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-500">GZ</span>
    </div>
  </div>
  
  <div className="flex flex-col">
    <span className="text-lg sm:text-xl font-black text-white tracking-tighter italic  leading-none">
      GenZ<span className="text-indigo-500">Evaluation</span>
    </span>
  </div>
</div>

          {/* DESKTOP GUEST LINKS */}
          <div className="hidden md:flex items-center space-x-1">
            <Link to="/" className="px-4 py-2 text-gray-400 hover:text-white text-[11px] font-black uppercase tracking-widest transition-all rounded-lg hover:bg-white/5 italic">Home</Link>
            <Link to="/competitions" className="px-4 py-2 text-gray-400 hover:text-white text-[11px] font-black uppercase tracking-widest transition-all rounded-lg hover:bg-white/5 italic">Competitions</Link>
            <Link to="/how-it-works" className="px-4 py-2 text-gray-400 hover:text-white text-[11px] font-black uppercase tracking-widest transition-all rounded-lg hover:bg-white/5 italic">Protocol</Link>
            
            <div className="relative" ref={supportRef}>
              <button onClick={() => setIsSupportOpen(!isSupportOpen)} className="flex items-center gap-1 px-4 py-2 text-gray-400 hover:text-white text-[11px] font-black uppercase tracking-widest transition-all rounded-lg hover:bg-white/5 italic outline-none">
                Support <ChevronDownIcon className={`h-3 w-3 transition-transform duration-300 ${isSupportOpen ? 'rotate-180' : ''}`} />
              </button>
              {isSupportOpen && (
                <div className="absolute left-0 mt-3 w-52 bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl py-2 z-50 overflow-hidden animate-in fade-in zoom-in-95">
                  <Link to="/about" className="flex items-center gap-3 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:bg-white/5 hover:text-white italic transition-colors">
                    <InformationCircleIcon className="h-4 w-4 text-indigo-500" /> About Us
                  </Link>
                  <Link to="/fqa" className="flex items-center gap-3 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:bg-white/5 hover:text-white italic transition-colors">
                    <QuestionMarkCircleIcon className="h-4 w-4 text-indigo-500" /> FAQ
                  </Link>
                  <Link to="/contact" className="flex items-center gap-3 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:bg-white/5 hover:text-white italic transition-colors">
                    <ChatBubbleLeftRightIcon className="h-4 w-4 text-indigo-500" /> Contact
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* GUEST AUTH ACTIONS */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="text-[11px] font-black uppercase tracking-widest text-gray-400 hover:text-white transition-colors italic">Login</Link>
            <Link to="/register" className="bg-white text-black hover:bg-indigo-600 hover:text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl italic">
              Register
            </Link>
          </div>

          {/* MOBILE TOGGLE */}
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-3 rounded-xl bg-white/5 text-gray-400 active:scale-90 transition-all border border-white/5">
              {isMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE GUEST MENU */}
      {isMenuOpen && (
        <div className="md:hidden bg-[#050505] border-t border-white/5 px-6 py-8 space-y-4 animate-in slide-in-from-top-4 duration-300">
          <Link to="/" className="block text-[12px] font-black uppercase tracking-widest text-gray-300 italic">Home</Link>
          <Link to="/competitions" className="block text-[12px] font-black uppercase tracking-widest text-gray-300 italic">Competitions</Link>
          <Link to="/how-it-works" className="block text-[12px] font-black uppercase tracking-widest text-gray-300 italic">Protocol</Link>
          <div className="h-px bg-white/5 my-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/login" className="flex justify-center py-4 rounded-xl border border-white/10 text-[10px] font-black uppercase tracking-widest text-white italic">Login</Link>
            <Link to="/register" className="flex justify-center py-4 rounded-xl bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest italic shadow-lg">Register</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;